import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { CalendarDays, MapPin, ArrowUpRight, Church, Lock, Clock, MessageCircle, Phone, Mail, Globe, Repeat, Send, Loader2, HeartHandshake, Star } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

const HERO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_cosecha-eventos/artifacts/xs7qt8ma_DJDJDJ.jpg";
const MANIFESTO_IMG =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_cosecha-eventos/artifacts/1f2tobm3_unnamed%20%281%29.webp";
const PASTORS_PHOTO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_cosecha-eventos/artifacts/cm2ujpki_unnamed.webp";

const ADDRESS = "6500 West 65th St, Chicago, IL 60638";
const MAP_URL = "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(ADDRESS);
const EMAIL = "Ministeriohashemadonay0624@gmail.com";

const heroLines = ["Cosecha", "Hashem", "Adonay"];

const VERSES = [
  { es: { t: "Porque de tal manera amó Dios al mundo, que ha dado a su Hijo unigénito, para que todo aquel que en él cree, no se pierda, mas tenga vida eterna.", r: "Juan 3:16" }, en: { t: "For God so loved the world, that he gave his only begotten Son, that whosoever believeth in him should not perish, but have everlasting life.", r: "John 3:16" } },
  { es: { t: "Todo lo puedo en Cristo que me fortalece.", r: "Filipenses 4:13" }, en: { t: "I can do all things through Christ which strengtheneth me.", r: "Philippians 4:13" } },
  { es: { t: "Jehová es mi pastor; nada me faltará.", r: "Salmos 23:1" }, en: { t: "The Lord is my shepherd; I shall not want.", r: "Psalm 23:1" } },
  { es: { t: "Fíate de Jehová de todo tu corazón, y no te apoyes en tu propia prudencia.", r: "Proverbios 3:5" }, en: { t: "Trust in the Lord with all thine heart; and lean not unto thine own understanding.", r: "Proverbs 3:5" } },
  { es: { t: "Esfuérzate y sé valiente; no temas ni desmayes, porque Jehová tu Dios estará contigo dondequiera que vayas.", r: "Josué 1:9" }, en: { t: "Be strong and of a good courage; be not afraid, neither be thou dismayed: for the Lord thy God is with thee whithersoever thou goest.", r: "Joshua 1:9" } },
  { es: { t: "Y sabemos que a los que aman a Dios, todas las cosas les ayudan a bien.", r: "Romanos 8:28" }, en: { t: "And we know that all things work together for good to them that love God.", r: "Romans 8:28" } },
  { es: { t: "Clama a mí, y yo te responderé, y te enseñaré cosas grandes y ocultas que tú no conoces.", r: "Jeremías 33:3" }, en: { t: "Call unto me, and I will answer thee, and shew thee great and mighty things, which thou knowest not.", r: "Jeremiah 33:3" } },
  { es: { t: "Bástate mi gracia; porque mi poder se perfecciona en la debilidad.", r: "2 Corintios 12:9" }, en: { t: "My grace is sufficient for thee: for my strength is made perfect in weakness.", r: "2 Corinthians 12:9" } },
  { es: { t: "El Señor es mi luz y mi salvación; ¿de quién temeré?", r: "Salmos 27:1" }, en: { t: "The Lord is my light and my salvation; whom shall I fear?", r: "Psalm 27:1" } },
  { es: { t: "Pero los que esperan a Jehová tendrán nuevas fuerzas; levantarán alas como las águilas.", r: "Isaías 40:31" }, en: { t: "But they that wait upon the Lord shall renew their strength; they shall mount up with wings as eagles.", r: "Isaiah 40:31" } },
  { es: { t: "Echando toda vuestra ansiedad sobre él, porque él tiene cuidado de vosotros.", r: "1 Pedro 5:7" }, en: { t: "Casting all your care upon him; for he careth for you.", r: "1 Peter 5:7" } },
  { es: { t: "Este es el día que hizo Jehová; nos gozaremos y alegraremos en él.", r: "Salmos 118:24" }, en: { t: "This is the day which the Lord hath made; we will rejoice and be glad in it.", r: "Psalm 118:24" } },
  { es: { t: "Mas buscad primeramente el reino de Dios y su justicia, y todas estas cosas os serán añadidas.", r: "Mateo 6:33" }, en: { t: "But seek ye first the kingdom of God, and his righteousness; and all these things shall be added unto you.", r: "Matthew 6:33" } },
  { es: { t: "Lámpara es a mis pies tu palabra, y lumbrera a mi camino.", r: "Salmos 119:105" }, en: { t: "Thy word is a lamp unto my feet, and a light unto my path.", r: "Psalm 119:105" } },
  { es: { t: "No se turbe vuestro corazón; creéis en Dios, creed también en mí.", r: "Juan 14:1" }, en: { t: "Let not your heart be troubled: ye believe in God, believe also in me.", r: "John 14:1" } },
];

function verseOfToday() {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((now - start) / 86400000);
  return VERSES[dayOfYear % VERSES.length];
}

const pastorsBase = [
  { phone: "847 254 6468", tel: "+18472546468", wa: "18472546468" },
  { phone: "708 737 8342", tel: "+17087378342", wa: "17087378342" },
];

const TR = {
  es: {
    langBtn: "English",
    verseOfDayOverline: "Versículo del Día",
    getDirections: "Cómo llegar",
    featuredOverline: "Evento Destacado",
    adminAccess: "Acceso administradores",
    heroOverline: "Ministerio Internacional La Cosecha",
    heroSubtitle:
      "Un espacio consagrado para adorar, servir y administrar con excelencia los eventos y recursos de nuestra congregación.",
    verse:
      "“Mira que te he puesto en este día sobre naciones y sobre reinos, para arrancar y para destruir, para arruinar y para derribar, para edificar y para plantar.”",
    verseRef: "Jeremías 1:10",
    marqueeTag: "Ministerio Internacional La Cosecha",
    manifestoOverline: "Nuestro Manifiesto",
    chapters: [
      { n: "01", title: "Nuestra Visión", body: "Alcanzar y discipular naciones para Cristo. Ganar · Consolidar · Discipular · Enviar." },
      { n: "02", title: "Nuestra Misión", body: "Formar hombres y mujeres íntegros que lleven el evangelio de Jesucristo hasta lo último de la tierra, con principios bíblicos como base de la familia y la sociedad." },
      { n: "03", title: "Nuestra Motivación", body: "Servir con amor, pasión y honestidad; liderar para producir frutos dignos, confiando que con la ayuda de Dios transformaremos ciudades y naciones." },
    ],
    servicesOverline: "Horarios de Servicio",
    servicesHeading: "Te Esperamos",
    pastorsLine: (
      <>
        Pastores <span className="text-foreground">Omar Valeriano</span> y{" "}
        <span className="text-foreground">Xotchitl García</span>
      </>
    ),
    services: [
      { day: "Domingo", items: [{ time: "2:30 PM", label: "Tiempo de Adoración" }, { time: "3:00 PM", label: "Culto" }] },
      { day: "Miércoles", items: [{ time: "6:30 PM", label: "Tiempo de Adoración" }, { time: "7:00 PM", label: "Culto" }] },
    ],
    eventsOverline: "Agenda",
    eventsHeading: "Próximos Eventos",
    eventsEmpty: "Aún no hay eventos publicados. Vuelve pronto para ver nuestra agenda.",
    eventsLoading: "Cargando eventos…",
    galleryOverline: "Galería",
    galleryHeading: "Vida de Nuestra Iglesia",
    recurringLabels: [
      "Todos los domingos",
      "Todos los lunes",
      "Todos los martes",
      "Todos los miércoles",
      "Todos los jueves",
      "Todos los viernes",
      "Todos los sábados",
    ],
    shareWhatsApp: "Compartir por WhatsApp",
    shareGalleryMsg: (title) =>
      `🙌 ${title ? `Mira "${title}" de` : "Mira la galería de"} nuestra iglesia Cosecha Hashem Adonay:\n\n${window.location.origin}/#galeria`,
    contactOverline: "Contacto",
    contactHeading: "Comunícate con Nosotros",
    prayerOverline: "Peticiones de Oración",
    prayerHeading: "¿Cómo podemos orar por ti?",
    prayerSubtitle: "Comparte tu petición con confianza. Nuestros pastores orarán por ti.",
    prayerName: "Tu nombre (opcional)",
    prayerMessage: "Escribe tu petición de oración…",
    prayerSend: "Enviar Petición",
    prayerSuccess: "Gracias, hemos recibido tu petición. Oraremos por ti. 🙏",
    prayerError: "Por favor escribe tu petición.",
    pastorsCaption: "Pastores Omar & Xotchitl",
    pastorNames: ["Pastor Omar Valeriano", "Pastora Xotchitl García"],
    sendWhatsApp: "Enviar WhatsApp",
    footerName: "Iglesia Cosecha Hashem Adonay",
    footerTagline: "Ministerio Internacional La Cosecha · Chicago, IL · Pastores Omar Valeriano y Xotchitl García",
    footerRights: "Todos los derechos reservados",
    shareMsg: (ev, dateLabel, timePart) =>
      `🙌 ¡Te invitamos a "${ev.name}" en Cosecha Hashem Adonay!\n\n📅 ${dateLabel}${timePart}\n📍 ${ev.location}\n\n${ev.description ? ev.description + "\n\n" : ""}${window.location.origin}`,
    shareAt: (t) => ` a las ${t}`,
  },
  en: {
    langBtn: "Español",
    verseOfDayOverline: "Verse of the Day",
    getDirections: "Get Directions",
    featuredOverline: "Featured Event",
    adminAccess: "Admin access",
    heroOverline: "La Cosecha International Ministry",
    heroSubtitle:
      "A consecrated space to worship, serve, and faithfully steward the events and resources of our congregation.",
    verse:
      "“See, I have this day set thee over the nations and over the kingdoms, to root out, and to pull down, and to destroy, and to throw down, to build, and to plant.”",
    verseRef: "Jeremiah 1:10",
    marqueeTag: "La Cosecha International Ministry",
    manifestoOverline: "Our Manifesto",
    chapters: [
      { n: "01", title: "Our Vision", body: "To reach and disciple the nations for Christ. Win · Consolidate · Disciple · Send." },
      { n: "02", title: "Our Mission", body: "To form men and women of integrity who carry the gospel of Jesus Christ to the ends of the earth, grounded in biblical principles for family and society." },
      { n: "03", title: "Our Motivation", body: "To serve with love, passion, and honesty; to lead in bearing worthy fruit, trusting that with God's help we will transform cities and nations." },
    ],
    servicesOverline: "Service Times",
    servicesHeading: "We Await You",
    pastorsLine: (
      <>
        Pastors <span className="text-foreground">Omar Valeriano</span> and{" "}
        <span className="text-foreground">Xotchitl García</span>
      </>
    ),
    services: [
      { day: "Sunday", items: [{ time: "2:30 PM", label: "Worship Time" }, { time: "3:00 PM", label: "Service" }] },
      { day: "Wednesday", items: [{ time: "6:30 PM", label: "Worship Time" }, { time: "7:00 PM", label: "Service" }] },
    ],
    eventsOverline: "Agenda",
    eventsHeading: "Upcoming Events",
    eventsEmpty: "No events published yet. Check back soon for our schedule.",
    eventsLoading: "Loading events…",
    galleryOverline: "Gallery",
    galleryHeading: "Life of Our Church",
    recurringLabels: [
      "Every Sunday",
      "Every Monday",
      "Every Tuesday",
      "Every Wednesday",
      "Every Thursday",
      "Every Friday",
      "Every Saturday",
    ],
    shareWhatsApp: "Share on WhatsApp",
    shareGalleryMsg: (title) =>
      `🙌 ${title ? `Check out "${title}" from` : "Check out the gallery of"} our church Cosecha Hashem Adonay:\n\n${window.location.origin}/#galeria`,
    contactOverline: "Contact",
    contactHeading: "Get in Touch",
    prayerOverline: "Prayer Requests",
    prayerHeading: "How can we pray for you?",
    prayerSubtitle: "Share your request with confidence. Our pastors will pray for you.",
    prayerName: "Your name (optional)",
    prayerMessage: "Write your prayer request…",
    prayerSend: "Send Request",
    prayerSuccess: "Thank you, we received your request. We will pray for you. 🙏",
    prayerError: "Please write your request.",
    pastorsCaption: "Pastors Omar & Xotchitl",
    pastorNames: ["Pastor Omar Valeriano", "Pastor Xotchitl García"],
    sendWhatsApp: "Send WhatsApp",
    footerName: "Cosecha Hashem Adonay Church",
    footerTagline: "La Cosecha International Ministry · Chicago, IL · Pastors Omar Valeriano & Xotchitl García",
    footerRights: "All rights reserved",
    shareMsg: (ev, dateLabel, timePart) =>
      `🙌 You're invited to "${ev.name}" at Cosecha Hashem Adonay!\n\n📅 ${dateLabel}${timePart}\n📍 ${ev.location}\n\n${ev.description ? ev.description + "\n\n" : ""}${window.location.origin}`,
    shareAt: (t) => ` at ${t}`,
  },
};

function formatTime(t) {
  if (!t) return "";
  const [h, m] = t.split(":");
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

function isVideoSrc(src) {
  if (!src) return false;
  return src.startsWith("data:video") || /\.(mp4|webm|mov|ogg)(\?|$)/i.test(src);
}

function EventMedia({ src, alt, className }) {
  if (!src) return null;
  if (isVideoSrc(src)) {
    return (
      <video src={src} className={className} autoPlay muted loop playsInline />
    );
  }
  return <img src={src} alt={alt} className={className} />;
}

function shareWhatsApp(ev, t) {
  const timeStr = ev.time
    ? formatTime(ev.time) + (ev.end_time ? ` – ${formatTime(ev.end_time)}` : "")
    : "";
  const timePart = timeStr ? t.shareAt(timeStr) : "";
  const dateLabel = ev.recurring ? t.recurringLabels[ev.weekday] : ev.date;
  const msg = t.shareMsg(ev, dateLabel, timePart);
  window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
}

const reveal = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

function useLenis() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
}

function Navbar({ t, lang, toggleLang }) {
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 backdrop-blur-xl bg-black/60">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
        <div className="flex items-center gap-2.5">
          <Church className="h-5 w-5 text-primary" />
          <span className="font-serif text-lg tracking-wide">Cosecha Hashem Adonay</span>
        </div>
        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={toggleLang}
            data-testid="lang-toggle"
            aria-label={`Cambiar idioma a ${t.langBtn}`}
            className="inline-flex items-center gap-2 rounded-full border border-primary/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            <Globe className="h-3.5 w-3.5" /> {t.langBtn}
          </button>
          <Link
            to="/login"
            data-testid="nav-login-link"
            aria-label={t.adminAccess}
            title={t.adminAccess}
            className="text-muted-foreground/50 transition-colors hover:text-primary"
          >
            <Lock className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero({ t }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-screen overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={HERO} alt="Congregación en adoración" className="h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-6">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="overline mb-6 text-primary"
        >
          {t.heroOverline}
        </motion.p>

        <h1 className="font-serif text-[18vw] leading-[0.82] tracking-tight text-foreground sm:text-8xl lg:text-[10rem]">
          {heroLines.map((line, i) => (
            <span key={line} className="block overflow-hidden">
              <motion.span
                className="inline-block"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.3 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                {i === 1 ? <span className="text-primary italic">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-md text-base text-muted-foreground"
        >
          {t.heroSubtitle}
        </motion.p>

        <motion.blockquote
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          data-testid="hero-verse"
          className="mt-10 max-w-2xl border-l-2 border-primary/60 pl-5"
        >
          <p className="font-serif text-lg italic leading-relaxed text-foreground/90 sm:text-xl">{t.verse}</p>
          <cite className="overline mt-3 block not-italic text-primary">{t.verseRef}</cite>
        </motion.blockquote>
      </motion.div>
    </section>
  );
}

function FeaturedBanner({ t, lang }) {
  const [featured, setFeatured] = useState([]);
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    api
      .get(`/events?lang=${lang}`)
      .then((r) => setFeatured((r.data || []).filter((e) => e.featured && e.image_url)))
      .catch(() => {});
  }, [lang]);

  // Auto-rotate between featured events every 7 seconds
  useEffect(() => {
    if (featured.length < 2) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % featured.length), 7000);
    return () => clearInterval(id);
  }, [featured.length]);

  if (featured.length === 0) return null;
  const ev = featured[idx % featured.length];
  const timeStr = ev.time
    ? formatTime(ev.time) + (ev.end_time ? ` – ${formatTime(ev.end_time)}` : "")
    : "";
  const dateLabel = ev.recurring ? t.recurringLabels[ev.weekday] : ev.date;

  return (
    <section className="relative overflow-hidden border-b border-border bg-black" data-testid="featured-banner">
      <AnimatePresence mode="wait">
        <motion.div
          key={ev.id}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
          className="mx-auto grid max-w-7xl grid-cols-1 gap-0 lg:grid-cols-2"
        >
          <div className="relative h-[420px] overflow-hidden lg:h-auto">
            <EventMedia src={ev.image_url} alt="" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-2xl" />
            <EventMedia src={ev.image_url} alt={ev.name} className="relative h-full w-full object-contain" />
          </div>
          <div className="flex flex-col justify-center p-10 lg:p-16">
            <span className="overline mb-5 inline-flex items-center gap-2 text-primary">
              <Star className="h-3.5 w-3.5" /> {t.featuredOverline}
            </span>
            <h2 className="font-serif text-5xl leading-[0.95] text-foreground sm:text-6xl">{ev.name}</h2>
            {ev.description && (
              <p className="mt-5 max-w-md text-muted-foreground">{ev.description}</p>
            )}
            <div className="mt-7 flex flex-wrap items-center gap-5 text-sm text-foreground/85">
              <span className="inline-flex items-center gap-2">
                {ev.recurring ? <Repeat className="h-4 w-4 text-primary" /> : <CalendarDays className="h-4 w-4 text-primary" />}
                {dateLabel}
              </span>
              {timeStr && (
                <span className="inline-flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" /> {timeStr}
                </span>
              )}
              <span className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" /> {ev.location}
              </span>
            </div>
            <button
              type="button"
              onClick={() => shareWhatsApp(ev, t)}
              data-testid="featured-share-button"
              className="mt-8 inline-flex w-fit items-center gap-2 rounded-full border border-[#25D366]/40 px-5 py-2.5 text-sm font-semibold text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-black"
            >
              <MessageCircle className="h-4 w-4" /> {t.shareWhatsApp}
            </button>
          </div>
        </motion.div>
      </AnimatePresence>
      {featured.length > 1 && (
        <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 gap-2.5" data-testid="featured-dots">
          {featured.map((f, i) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setIdx(i)}
              data-testid={`featured-dot-${i}`}
              aria-label={f.name}
              className={`h-2 rounded-full transition-all duration-500 ${
                i === idx % featured.length ? "w-7 bg-primary" : "w-2 bg-white/30 hover:bg-white/60"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function Marquee({ t }) {
  const items = Array(6).fill(`Cosecha Hashem Adonay · ${t.marqueeTag} · `);
  return (
    <div className="border-y border-border bg-black py-6 overflow-hidden">
      <div className="marquee-track">
        {[0, 1].map((k) => (
          <span key={k} className="font-serif text-4xl italic text-primary/80 sm:text-5xl">
            {items.join("")}
          </span>
        ))}
      </div>
    </div>
  );
}

function VerseOfDay({ t, lang }) {
  const v = verseOfToday()[lang];
  return (
    <section className="relative overflow-hidden bg-[#080808] py-24 grain" data-testid="verse-of-day-section">
      <div className="mx-auto max-w-4xl px-6 text-center">
        <p className="overline mb-8 text-primary">{t.verseOfDayOverline}</p>
        <motion.blockquote
          key={v.r}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-serif text-3xl italic leading-snug text-foreground sm:text-4xl lg:text-5xl">
            “{v.t}”
          </p>
          <cite className="overline mt-8 block not-italic text-primary">{v.r}</cite>
        </motion.blockquote>
      </div>
    </section>
  );
}

function Manifesto({ t }) {
  return (
    <section className="mx-auto max-w-7xl px-6 py-28" data-testid="manifesto-section">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
        <motion.div
          initial={{ clipPath: "inset(100% 0 0 0)" }}
          whileInView={{ clipPath: "inset(0% 0 0 0)" }}
          viewport={{ once: true }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="relative h-[520px] overflow-hidden rounded-sm border border-border bg-black"
        >
          <img src={MANIFESTO_IMG} alt="Logo Ministerio Internacional La Cosecha" className="h-full w-full object-contain p-8" />
        </motion.div>

        <div className="flex flex-col justify-center">
          <p className="overline mb-10 text-primary">{t.manifestoOverline}</p>
          {t.chapters.map((c) => (
            <motion.div
              key={c.n}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="border-t border-border py-8"
            >
              <div className="flex gap-6">
                <span className="font-serif text-2xl text-primary/70">{c.n}</span>
                <div>
                  <h3 className="font-serif text-3xl text-foreground">{c.title}</h3>
                  <p className="mt-3 text-muted-foreground">{c.body}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Services({ t }) {
  return (
    <section className="mx-auto max-w-7xl px-6 py-28" data-testid="services-section">
      <div className="mb-14 text-center">
        <p className="overline mb-4 text-primary">{t.servicesOverline}</p>
        <h2 className="font-serif text-5xl leading-none sm:text-6xl">{t.servicesHeading}</h2>
        <p className="mt-6 text-sm text-muted-foreground" data-testid="pastors-line">
          {t.pastorsLine}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {t.services.map((s, idx) => (
          <motion.div
            key={idx}
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            data-testid={`service-${idx}`}
            className="rounded-sm border border-border bg-card p-8 transition-colors hover:border-primary/50"
          >
            <h3 className="mb-6 font-serif text-3xl text-primary">{s.day}</h3>
            <ul className="space-y-4">
              {s.items.map((it) => (
                <li key={it.time + it.label} className="flex items-center gap-4 border-t border-border pt-4">
                  <Clock className="h-5 w-5 shrink-0 text-primary" />
                  <span className="w-24 font-semibold text-foreground">{it.time}</span>
                  <span className="text-muted-foreground">{it.label}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>

      <motion.a
        href={MAP_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="location-link"
        variants={reveal}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
        className="mt-8 flex items-center justify-center gap-3 rounded-sm border border-primary/30 bg-primary/5 px-6 py-5 text-center transition-colors hover:bg-primary/10"
      >
        <MapPin className="h-5 w-5 text-primary" />
        <span className="font-medium text-foreground">{ADDRESS}</span>
        <ArrowUpRight className="h-4 w-4 text-primary" />
      </motion.a>
    </section>
  );
}

function Events({ t, lang }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setLoading(true);
    api
      .get(`/events?lang=${lang}`)
      .then((r) => setEvents(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [lang]);

  return (
    <section className="relative overflow-hidden bg-[#080808] py-28" data-testid="events-section">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-14 flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <p className="overline mb-4 text-primary">{t.eventsOverline}</p>
            <h2 className="font-serif text-5xl leading-none sm:text-6xl">{t.eventsHeading}</h2>
          </div>
        </div>

        {loading ? (
          <div className="rounded-sm border border-dashed border-border p-16 text-center text-muted-foreground" data-testid="events-loading">
            {t.eventsLoading}
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-sm border border-dashed border-border p-16 text-center text-muted-foreground">
            {t.eventsEmpty}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {events.slice(0, 6).map((ev, i) => (
              <motion.article
                key={ev.id}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                data-testid={`public-event-card-${ev.id}`}
                className="group relative overflow-hidden rounded-sm border border-border bg-card transition-colors hover:border-primary/50"
              >
                {ev.image_url && (
                  <div className="relative h-52 overflow-hidden">
                    <EventMedia
                      src={ev.image_url}
                      alt=""
                      className="absolute inset-0 h-full w-full scale-110 object-cover opacity-40 blur-xl"
                    />
                    <EventMedia
                      src={ev.image_url}
                      alt={ev.name}
                      className="relative h-full w-full object-contain"
                    />
                  </div>
                )}
                <div className="relative p-7">
                <ArrowUpRight className="absolute right-5 top-5 h-5 w-5 text-muted-foreground transition-colors group-hover:text-primary" />
                <p className="overline mb-6 text-primary/70">
                  {ev.recurring ? t.recurringLabels[ev.weekday] : ev.date}
                </p>
                <h3 className="font-serif text-3xl leading-tight text-foreground">{ev.name}</h3>
                <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{ev.description}</p>
                <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-border pt-5 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    {ev.recurring ? (
                      <>
                        <Repeat className="h-3.5 w-3.5" /> {t.recurringLabels[ev.weekday]}
                      </>
                    ) : (
                      <>
                        <CalendarDays className="h-3.5 w-3.5" /> {ev.date}
                      </>
                    )}
                  </span>
                  {ev.time && (
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" /> {formatTime(ev.time)}
                      {ev.end_time ? ` – ${formatTime(ev.end_time)}` : ""}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5" /> {ev.location}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => shareWhatsApp(ev, t)}
                  data-testid={`share-event-${ev.id}`}
                  className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#25D366]/40 px-4 py-2 text-xs font-semibold text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-black"
                >
                  <MessageCircle className="h-4 w-4" /> {t.shareWhatsApp}
                </button>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Gallery({ t }) {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  useEffect(() => {
    api.get("/gallery").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const shareItem = (item) => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(t.shareGalleryMsg(item.title))}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  if (items.length === 0) return null;
  return (
    <section id="galeria" className="border-t border-border py-24 sm:py-32" data-testid="gallery-section">
      <div className="mx-auto max-w-7xl px-6">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end"
        >
          <div>
            <p className="overline mb-4 text-primary">{t.galleryOverline}</p>
            <h2 className="font-serif text-5xl leading-none sm:text-6xl">{t.galleryHeading}</h2>
          </div>
        </motion.div>

        <div className="mt-14 grid grid-cols-2 gap-4 md:grid-cols-3">
          {items.map((item, i) => (
            <motion.button
              key={item.id}
              type="button"
              onClick={() => setActive(item)}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              data-testid={`gallery-item-${item.id}`}
              className="group relative aspect-[4/3] overflow-hidden rounded-sm border border-border bg-black transition-colors hover:border-primary/50"
            >
              <EventMedia
                src={item.media_url}
                alt={item.title || t.galleryOverline}
                className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              {item.title && (
                <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-4 pb-3 pt-10 text-left text-sm text-foreground/90">
                  {item.title}
                </span>
              )}
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  shareItem(item);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    shareItem(item);
                  }
                }}
                data-testid={`share-gallery-${item.id}`}
                title={t.shareWhatsApp}
                className="absolute right-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#25D366]/50 bg-black/60 text-[#25D366] opacity-0 backdrop-blur-sm transition-all hover:bg-[#25D366] hover:text-black group-hover:opacity-100"
              >
                <MessageCircle className="h-4 w-4" />
              </span>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-4xl border-border bg-black p-3" data-testid="gallery-lightbox">
          <DialogTitle className="sr-only">{active?.title || t.galleryOverline}</DialogTitle>
          {active && (
            <EventMedia
              src={active.media_url}
              alt={active.title || t.galleryOverline}
              className="max-h-[80vh] w-full object-contain"
            />
          )}
          {active?.title && (
            <p className="px-2 pb-2 pt-1 text-center text-sm text-muted-foreground">{active.title}</p>
          )}
          {active && (
            <button
              type="button"
              onClick={() => shareItem(active)}
              data-testid="gallery-lightbox-share"
              className="mx-auto mb-2 mt-1 inline-flex w-fit items-center gap-2 rounded-full border border-[#25D366]/40 px-5 py-2.5 text-sm font-semibold text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-black"
            >
              <MessageCircle className="h-4 w-4" /> {t.shareWhatsApp}
            </button>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}

function PrayerSection({ t }) {
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) {
      setError(t.prayerError);
      return;
    }
    setError("");
    setSending(true);
    try {
      await api.post("/prayer-requests", { name: name.trim(), message: message.trim() });
      setSent(true);
      setName("");
      setMessage("");
    } catch (err) {
      setError(t.prayerError);
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mx-auto max-w-3xl px-6 py-28" data-testid="prayer-section">
      <div className="mb-10 text-center">
        <p className="overline mb-4 text-primary">{t.prayerOverline}</p>
        <h2 className="font-serif text-5xl leading-none sm:text-6xl">{t.prayerHeading}</h2>
        <p className="mt-6 text-sm text-muted-foreground">{t.prayerSubtitle}</p>
      </div>

      {sent ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="prayer-success"
          className="flex flex-col items-center gap-4 rounded-sm border border-primary/30 bg-primary/5 p-12 text-center"
        >
          <HeartHandshake className="h-10 w-10 text-primary" />
          <p className="font-serif text-2xl text-foreground">{t.prayerSuccess}</p>
        </motion.div>
      ) : (
        <motion.form
          onSubmit={submit}
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="space-y-4 rounded-sm border border-border bg-card p-8"
        >
          <Input
            data-testid="prayer-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t.prayerName}
            className="bg-muted"
          />
          <Textarea
            data-testid="prayer-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t.prayerMessage}
            rows={5}
            className="bg-muted"
          />
          {error && <p className="text-sm text-destructive" data-testid="prayer-error">{error}</p>}
          <Button
            type="submit"
            disabled={sending}
            data-testid="prayer-submit-button"
            className="w-full rounded-full bg-primary text-primary-foreground hover:bg-[#E5B54B]"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="mr-2 h-4 w-4" /> {t.prayerSend}</>)}
          </Button>
        </motion.form>
      )}
    </section>
  );
}

function Contact({ t }) {
  return (
    <section className="border-t border-border bg-[#080808] py-28" data-testid="contact-section">
      <div className="mx-auto max-w-7xl px-6">
        <div className="mb-14 text-center">
          <p className="overline mb-4 text-primary">{t.contactOverline}</p>
          <h2 className="font-serif text-5xl leading-none sm:text-6xl">{t.contactHeading}</h2>
          <motion.figure variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto mt-8 w-40">
            <img
              src={PASTORS_PHOTO}
              alt={t.pastorsCaption}
              data-testid="pastors-photo"
              className="aspect-square w-40 rounded-full border border-primary/30 object-cover object-top shadow-lg"
            />
            <figcaption className="mt-3 text-xs text-muted-foreground">{t.pastorsCaption}</figcaption>
          </motion.figure>
        </div>

        <div className="mx-auto grid max-w-3xl grid-cols-1 gap-6 md:grid-cols-2">
          {pastorsBase.map((p, idx) => (
            <motion.div
              key={p.wa}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              data-testid={`contact-${p.wa}`}
              className="rounded-sm border border-border bg-card p-8 text-center transition-colors hover:border-primary/50"
            >
              <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-primary/40 text-primary">
                <Phone className="h-5 w-5" />
              </div>
              <h3 className="font-serif text-2xl text-foreground">{t.pastorNames[idx]}</h3>
              <a
                href={`tel:${p.tel}`}
                data-testid={`call-${p.wa}`}
                className="mt-2 block text-lg text-muted-foreground transition-colors hover:text-primary"
              >
                {p.phone}
              </a>
              <a
                href={`https://wa.me/${p.wa}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`whatsapp-${p.wa}`}
                className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#25D366]/40 px-4 py-2 text-xs font-semibold text-[#25D366] transition-colors hover:bg-[#25D366] hover:text-black"
              >
                <MessageCircle className="h-4 w-4" /> {t.sendWhatsApp}
              </a>
            </motion.div>
          ))}
        </div>

        <motion.a
          href={`mailto:${EMAIL}`}
          data-testid="contact-email"
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="mx-auto mt-6 flex max-w-3xl items-center justify-center gap-3 rounded-sm border border-primary/30 bg-primary/5 px-6 py-5 text-center transition-colors hover:bg-primary/10"
        >
          <Mail className="h-5 w-5 shrink-0 text-primary" />
          <span className="break-all font-medium text-foreground">{EMAIL}</span>
        </motion.a>
      </div>
    </section>
  );
}

function Footer({ t }) {
  return (
    <footer className="border-t border-border bg-black py-16">
      <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2.5">
          <Church className="h-5 w-5 text-primary" />
          <div>
            <span className="font-serif text-xl">{t.footerName}</span>
            <p className="text-xs text-muted-foreground">{t.footerTagline}</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          © {new Date().getFullYear()} Cosecha Hashem Adonay · {t.footerRights}
        </p>
      </div>
    </footer>
  );
}

export default function Landing() {
  useLenis();
  const [lang, setLang] = useState("es");
  const t = TR[lang];
  const toggleLang = () => setLang((l) => (l === "es" ? "en" : "es"));

  return (
    <div className="bg-background">
      <Navbar t={t} lang={lang} toggleLang={toggleLang} />
      <Hero t={t} />
      <FeaturedBanner t={t} lang={lang} />
      <Marquee t={t} />
      <VerseOfDay t={t} lang={lang} />
      <Manifesto t={t} />
      <Services t={t} />
      <Events t={t} lang={lang} />
      <Gallery t={t} />
      <PrayerSection t={t} />
      <Contact t={t} />
      <Footer t={t} />
    </div>
  );
}
